// Top-level error boundary. Catches render errors anywhere below it,
// logs to the console (Sentry's browser SDK would auto-capture if wired),
// and shows a recoverable fallback so the whole SPA doesn't go blank.

import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string }): void {
    console.error("[ErrorBoundary]", error, info);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  private handleReload = (): void => {
    window.location.assign("/");
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="auth-shell">
          <div className="auth-card" role="alert">
            <h1 className="auth-title">Something went wrong</h1>
            <p className="muted">
              The page hit an unexpected error. You can try again, or reload
              the app.
            </p>
            <pre className="codeblock" style={{ marginTop: 12 }}>
              {this.state.error.message}
            </pre>
            <div className="dialog-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-ghost btn-md"
                onClick={this.handleReset}
              >
                Try again
              </button>
              <button
                type="button"
                className="btn btn-primary btn-md"
                onClick={this.handleReload}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
